import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '@/common/ui/dialog'
import { Button } from '@/common/ui/button'
import { Badge } from '@/common/ui/badge'
import { Checkbox } from '@/common/ui/checkbox'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/common/ui/tabs'
import { TagIcon, X, Plus, Minus } from 'lucide-react'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import useMutationAssignTagsToPlayers from '@/modules/tags/api/hooks/useMutationAssignTagsToPlayers'
import useMutationRemoveTagsFromPlayers from '@/modules/tags/api/hooks/useMutationRemoveTagsFromPlayers'
import useQueryGetPlayerTags from '@/modules/tags/api/hooks/useQueryGetPlayerTags'
import { TagsQueryKeys } from '@/modules/tags/constants'
import type { PlayerTagResponse } from '@/modules/tags/types'
import { PlayerQueryKeys } from '@/modules/manage-player/keys'

interface AssignPlayerTagsModalProps {
	playerId: string
	playerPhone: string
	currentTags?: PlayerTagResponse[]
}

export default function AssignPlayerTagsModal({
	playerId,
	playerPhone,
	currentTags = [],
}: AssignPlayerTagsModalProps) {
	const [open, setOpen] = useState(false)
	const queryClient = useQueryClient()
	const [selectedAddTagIds, setSelectedAddTagIds] = useState<string[]>([])
	const [selectedRemoveTagIds, setSelectedRemoveTagIds] = useState<string[]>([])
	const [isLoading, setIsLoading] = useState(false)
	const [activeTab, setActiveTab] = useState('add')

	// Fetch all available tags
	const { data: allTags = [], isLoading: isLoadingTags } =
		useQueryGetPlayerTags()

	const { mutateAsync: assignTags } = useMutationAssignTagsToPlayers()
	const { mutateAsync: removeTags } = useMutationRemoveTagsFromPlayers()

	const handleToggleAddTag = (tagId: string) => {
		setSelectedAddTagIds((prev) =>
			prev.includes(tagId)
				? prev.filter((id) => id !== tagId)
				: [...prev, tagId],
		)
	}

	const handleToggleRemoveTag = (tagId: string) => {
		setSelectedRemoveTagIds((prev) =>
			prev.includes(tagId)
				? prev.filter((id) => id !== tagId)
				: [...prev, tagId],
		)
	}

	const handleAssign = async () => {
		if (selectedAddTagIds.length === 0) {
			toast.error('กรุณาเลือกแท็กอย่างน้อย 1 แท็ก')
			return
		}

		try {
			setIsLoading(true)

			await assignTags({
				payload: {
					playerIds: [playerId],
					tagIds: selectedAddTagIds,
				},
			})

			toast.success('เพิ่มแท็กให้ผู้เล่นสำเร็จ')

			// Invalidate queries to refresh data
			queryClient.invalidateQueries({
				queryKey: [TagsQueryKeys.GetPlayersByTags],
			})
			queryClient.invalidateQueries({
				queryKey: [TagsQueryKeys.GetTags],
			})
			queryClient.invalidateQueries({
				queryKey: [TagsQueryKeys.GetTagStatistics],
			})
			queryClient.invalidateQueries({
				queryKey: [PlayerQueryKeys.GetPlayerList],
			})

			setSelectedAddTagIds([])
			setOpen(false)
		} catch (error) {
			showErrorToast(error as Error)
		} finally {
			setIsLoading(false)
		}
	}

	const handleRemove = async () => {
		if (selectedRemoveTagIds.length === 0) {
			toast.error('กรุณาเลือกแท็กที่ต้องการลบอย่างน้อย 1 แท็ก')
			return
		}

		try {
			setIsLoading(true)

			await removeTags({
				payload: {
					playerIds: [playerId],
					tagIds: selectedRemoveTagIds,
				},
			})

			toast.success(`ลบแท็ก ${selectedRemoveTagIds.length} รายการสำเร็จ`)

			// Invalidate queries to refresh data
			queryClient.invalidateQueries({
				queryKey: [TagsQueryKeys.GetPlayersByTags],
			})
			queryClient.invalidateQueries({
				queryKey: [TagsQueryKeys.GetTags],
			})
			queryClient.invalidateQueries({
				queryKey: [TagsQueryKeys.GetTagStatistics],
			})
			queryClient.invalidateQueries({
				queryKey: [PlayerQueryKeys.GetPlayerList],
			})

			setSelectedRemoveTagIds([])
			setOpen(false)
		} catch (error) {
			showErrorToast(error as Error)
		} finally {
			setIsLoading(false)
		}
	}

	// Filter out tags that player already has
	const currentTagIds = currentTags.map((tag) => tag.id)

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogTrigger asChild>
				<Button variant="outline" size="sm">
					<TagIcon className="mr-2 h-4 w-4" />
					จัดการแท็ก
				</Button>
			</DialogTrigger>
			<DialogContent className="max-w-3xl">
				<DialogHeader>
					<DialogTitle>จัดการแท็กผู้เล่น</DialogTitle>
					<DialogDescription>
						เพิ่มหรือลบแท็กสำหรับผู้เล่น {playerPhone}
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4">
					{/* Current Tags */}
					{currentTags.length > 0 && (
						<div>
							<p className="mb-2 text-sm font-medium">
								แท็กปัจจุบัน ({currentTags.length})
							</p>
							<div className="flex flex-wrap gap-2">
								{currentTags.map((tag) => (
									<Badge
										key={tag.id}
										variant="outline"
										className="text-sm"
										style={{
											borderColor: tag.color,
											color: tag.color,
										}}
									>
										<span
											className="mr-2 h-2 w-2 rounded-full"
											style={{ backgroundColor: tag.color }}
										/>
										{tag.name}
									</Badge>
								))}
							</div>
						</div>
					)}

					<Tabs value={activeTab} onValueChange={setActiveTab}>
						<TabsList className="grid w-full grid-cols-2">
							<TabsTrigger value="add" className="flex items-center gap-2">
								<Plus className="h-4 w-4" />
								เพิ่มแท็ก
							</TabsTrigger>
							<TabsTrigger
								value="remove"
								className="flex items-center gap-2"
								disabled={currentTags.length === 0}
							>
								<Minus className="h-4 w-4" />
								ลบแท็ก
							</TabsTrigger>
						</TabsList>

						<TabsContent value="add" className="space-y-4">
							{/* Available Tags to Add */}
							<div>
								<p className="mb-2 text-sm font-medium">
									เลือกแท็กที่ต้องการเพิ่ม
								</p>
								{isLoadingTags ? (
									<p className="text-sm text-muted-foreground">
										กำลังโหลดแท็ก...
									</p>
								) : (
									(() => {
										const availableTags = allTags.filter(
											(tag) => !currentTagIds.includes(tag.id),
										)
										return availableTags.length === 0 ? (
											<p className="text-sm text-muted-foreground">
												{allTags.length === 0
													? 'ไม่มีแท็กในระบบ'
													: 'ผู้เล่นมีแท็กครบทุกแท็กแล้ว'}
											</p>
										) : (
											<div className="max-h-[300px] space-y-2 overflow-y-auto rounded-lg border p-4">
												{availableTags.map((tag) => {
													const isSelected = selectedAddTagIds.includes(tag.id)

													return (
														<div
															key={tag.id}
															className="flex items-center space-x-3 rounded-md p-2 hover:bg-muted/50"
														>
															<Checkbox
																id={`add-tag-${tag.id}`}
																checked={isSelected}
																onCheckedChange={() =>
																	handleToggleAddTag(tag.id)
																}
															/>
															<label
																htmlFor={`add-tag-${tag.id}`}
																className="flex flex-1 cursor-pointer items-center justify-between"
															>
																<div className="flex items-center gap-2">
																	<Badge
																		variant="outline"
																		className="text-sm"
																		style={{
																			borderColor: tag.color,
																			color: tag.color,
																		}}
																	>
																		<span
																			className="mr-2 h-2 w-2 rounded-full"
																			style={{ backgroundColor: tag.color }}
																		/>
																		{tag.name}
																	</Badge>
																</div>
																{tag.minDepositAmount && (
																	<span className="text-xs text-muted-foreground">
																		ฝากขั้นต่ำ:{' '}
																		{tag.minDepositAmount.toLocaleString()} บาท
																	</span>
																)}
															</label>
														</div>
													)
												})}
											</div>
										)
									})()
								)}
							</div>

							{/* Selected Tags to Add Preview */}
							{selectedAddTagIds.length > 0 && (
								<div>
									<p className="mb-2 text-sm font-medium">
										แท็กที่เลือกเพิ่ม ({selectedAddTagIds.length})
									</p>
									<div className="flex flex-wrap gap-2">
										{selectedAddTagIds.map((tagId) => {
											const tag = allTags.find((t) => t.id === tagId)
											if (!tag) return null
											return (
												<Badge
													key={tagId}
													variant="outline"
													className="text-sm"
													style={{
														borderColor: tag.color,
														color: tag.color,
													}}
												>
													<span
														className="mr-2 h-2 w-2 rounded-full"
														style={{ backgroundColor: tag.color }}
													/>
													{tag.name}
													<button
														type="button"
														onClick={() => handleToggleAddTag(tagId)}
														className="ml-2 hover:opacity-70"
													>
														<X className="h-3 w-3" />
													</button>
												</Badge>
											)
										})}
									</div>
								</div>
							)}
						</TabsContent>

						<TabsContent value="remove" className="space-y-4">
							{/* Current Tags to Remove */}
							<div>
								<p className="mb-2 text-sm font-medium">
									เลือกแท็กที่ต้องการลบ
								</p>
								{currentTags.length === 0 ? (
									<p className="text-sm text-muted-foreground">
										ผู้เล่นไม่มีแท็ก
									</p>
								) : (
									<div className="max-h-[300px] space-y-2 overflow-y-auto rounded-lg border p-4">
										{currentTags.map((tag) => {
											const isSelected = selectedRemoveTagIds.includes(tag.id)

											return (
												<div
													key={tag.id}
													className="flex items-center space-x-3 rounded-md p-2 hover:bg-muted/50"
												>
													<Checkbox
														id={`remove-tag-${tag.id}`}
														checked={isSelected}
														onCheckedChange={() =>
															handleToggleRemoveTag(tag.id)
														}
													/>
													<label
														htmlFor={`remove-tag-${tag.id}`}
														className="flex flex-1 cursor-pointer items-center justify-between"
													>
														<div className="flex items-center gap-2">
															<Badge
																variant="outline"
																className="text-sm"
																style={{
																	borderColor: tag.color,
																	color: tag.color,
																}}
															>
																<span
																	className="mr-2 h-2 w-2 rounded-full"
																	style={{ backgroundColor: tag.color }}
																/>
																{tag.name}
															</Badge>
														</div>
														{tag.minDepositAmount && (
															<span className="text-xs text-muted-foreground">
																ฝากขั้นต่ำ:{' '}
																{tag.minDepositAmount.toLocaleString()} บาท
															</span>
														)}
													</label>
												</div>
											)
										})}
									</div>
								)}
							</div>

							{/* Selected Tags to Remove Preview */}
							{selectedRemoveTagIds.length > 0 && (
								<div>
									<p className="mb-2 text-sm font-medium text-destructive">
										แท็กที่เลือกลบ ({selectedRemoveTagIds.length})
									</p>
									<div className="flex flex-wrap gap-2">
										{selectedRemoveTagIds.map((tagId) => {
											const tag = currentTags.find((t) => t.id === tagId)
											if (!tag) return null
											return (
												<Badge
													key={tagId}
													variant="destructive"
													className="text-sm"
												>
													<span className="mr-2 h-2 w-2 rounded-full bg-white" />
													{tag.name}
													<button
														type="button"
														onClick={() => handleToggleRemoveTag(tagId)}
														className="ml-2 hover:opacity-70"
													>
														<X className="h-3 w-3" />
													</button>
												</Badge>
											)
										})}
									</div>
								</div>
							)}
						</TabsContent>
					</Tabs>
				</div>

				<DialogFooter>
					<Button
						type="button"
						variant="outline"
						onClick={() => setOpen(false)}
						disabled={isLoading}
					>
						ยกเลิก
					</Button>
					{activeTab === 'add' ? (
						<Button
							type="button"
							onClick={handleAssign}
							disabled={isLoading || selectedAddTagIds.length === 0}
						>
							{isLoading ? 'กำลังเพิ่ม...' : 'เพิ่มแท็ก'}
						</Button>
					) : (
						<Button
							type="button"
							variant="destructive"
							onClick={handleRemove}
							disabled={isLoading || selectedRemoveTagIds.length === 0}
						>
							{isLoading ? 'กำลังลบ...' : 'ลบแท็ก'}
						</Button>
					)}
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
